import { DOCUMENT, Location, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { RouterLink } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { interval, map, Observable, Subscription, timer } from 'rxjs';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { RouteTranslatePipe } from 'src/app/_shared/pipes/route-translate.pipe';
import { VideoLoaderDirective } from 'src/app/common/video-loader/video-loader.directive';
import { ViewportDirective } from 'src/app/common/viewport/viewport.directive';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { SharrylandCherriesGqlGQL } from './homepage.query.generated';
import { environment } from '../../../environments/environment';
import { BasePlusComponent } from '../../_shared/components/base/base-plus.component';

@Component({
  selector: 'sharry-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    I18NextModule,
    MatButtonModule,
    IconComponent,
    MatExpansionModule,
    RouterLink,
    RouteTranslatePipe,
    ViewportDirective,
    VideoLoaderDirective,
    FooterComponent,
    HeaderComponent,
  ],
})
export class HomepageComponent extends BasePlusComponent implements OnInit, OnDestroy, AfterViewInit {
  totalCherries = 0;
  totalCherries$: Observable<number[]>;

  expandedIndex = 1;
  expandedIndexBottom = 4;
  changeExploreSubscription = new Subscription();
  indexHero = 0;
  indexCommunity = 0;
  showPaladins: boolean = environment.showPaladin;

  constructor(
    private sharrylandCherriesGQL: SharrylandCherriesGqlGQL,
    private renderer: Renderer2,
    private cd: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    private location: Location,
  ) {
    super();
    this.document.querySelector('head').insertAdjacentHTML(
      'beforeend',
      `<link
      rel="preload"
      href="https://media.sharry.land/app/images/homepage/hero/01-foto-480px.webp"
      as="image"
      media="(max-width: 480px)" />
    <link
      rel="preload"
      href="https://media.sharry.land/app/images/homepage/hero/01-foto-768px.webp"
      as="image"
      media="(min-width: 481px) and (max-width: 767px)" />
    <link
      rel="preload"
      href="https://media.sharry.land/app/images/homepage/hero/01-foto-1024px.webp"
      as="image"
      media="(min-width: 768px) and (max-width: 1023px)" />
    <link
      rel="preload"
      href="https://media.sharry.land/app/images/homepage/hero/01-foto-1440px.webp"
      as="image"
      media="(min-width: 1024px)" />`,
    );
  }

  ngOnInit() {
    this.metadataService.setMetadataPage('wonders-map', this.location.path());
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
    this.totalCherries$ = this.sharrylandCherriesGQL
      .fetch({}, { fetchPolicy: 'no-cache' })
      .pipe(map(r => this.numberToArray(r.data.sharrylandCherries)));
  }

  ngAfterViewInit() {
    if (this.isPlatformBrowser()) {
      this.subscriptions.push(
        timer(200).subscribe({
          next: () => {
            this.indexHero = 1;
            this.indexCommunity = 1;
            this.cd.detectChanges();
          },
        }),
        interval(6000).subscribe({
          next: () => {
            this.changeCommunityBackground();
            if (this.indexHero < 4) {
              this.indexHero++;
            } else {
              this.indexHero = 1;
            }
            this.indexCommunity = this.indexCommunity === 1 ? 2 : 1;
            this.cd.detectChanges();
          },
        }),
        this.startSubsChangeExplore(),
      );
    }
  }

  onChangeExploreBackground() {
    this.changeExploreSubscription.unsubscribe();
    this.startSubsChangeExplore();
  }

  startSubsChangeExplore() {
    return (this.changeExploreSubscription = interval(10000).subscribe({
      next: () => {
        this.changeExplore();
      },
    }));
  }

  changeCommunityBackground() {
    const element = this.document.querySelector('.homepage__community__container');
    if (element) {
      if (element.classList.contains('homepage__community__container--boy')) {
        this.renderer.removeClass(element, 'homepage__community__container--boy');
        this.renderer.addClass(element, 'homepage__community__container--girl');
      } else {
        this.renderer.removeClass(element, 'homepage__community__container--girl');
        this.renderer.addClass(element, 'homepage__community__container--boy');
      }
    }
  }

  changeExplore() {
    if (this.expandedIndex < 3) {
      this.expandedIndex++;
    } else {
      this.expandedIndex = 1;
    }
    if (this.expandedIndexBottom < 6) {
      this.expandedIndexBottom++;
    } else {
      this.expandedIndexBottom = 4;
    }
    this.cd.detectChanges();
  }

  numberToArray(num: number): number[] {
    return Intl.NumberFormat('it-IT', { minimumIntegerDigits: 6, useGrouping: false })
      .format(num)
      .split('')
      .map(n => Number(n));
  }

  onChangeImage(index: number, section: 'top' | 'bottom') {
    switch (section) {
      case 'top':
        this.expandedIndex = index;
        break;
      case 'bottom':
        this.expandedIndexBottom = index;
        break;
    }
    this.cd.detectChanges();
  }

  scroll() {
    this.document
      .querySelector('[id="cherry"]')
      .scrollIntoView({ inline: 'start', block: 'start', behavior: 'smooth' });
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
  }
}
